import io from 'socket.io-client';
import { devLog, devWarn, devError } from '../utils/devLogger';
import { 
  AuctionDetailView, 
  ResponseModel
} from '../types/socket.types';type TypedSocket = ReturnType<typeof io>;

class AuctionSocketService {
  private socket: TypedSocket | null = null;
  private isConnected = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private accessToken: string | null = null;
  private tokenType: 'user' | 'admin' = 'user'; // 토큰 타입 추가
  private isDebugMode = process.env.NODE_ENV === 'development' || 
                        window.location.hostname === 'localhost' || 
                        window.location.hostname === '127.0.0.1';

  // 이벤트 리스너들을 저장할 Map
  private eventListeners = new Map<string, Function[]>();

  // 디버그 로깅 함수
  private debugLog(message: string, data?: any) {
    if (this.isDebugMode) {
      devLog(message, data);
    }
  }

  // 토큰을 설정하고 소켓을 초기화하는 메서드 (개선된 버전)
  setToken(token: string | null, tokenType: 'user' | 'admin' = 'user') {
    this.accessToken = token;
    this.tokenType = tokenType;
    
    this.debugLog('🔑 [AuctionSocketService] 토큰 설정:', {
      hasToken: !!token,
      tokenType,
      tokenLength: token?.length || 0,
      tokenPreview: token ? token.substring(0, 20) + '...' : 'null'
    });
    
    // 토큰이 있든 없든 소켓을 초기화 (서버에서 게스트 연결 지원)
    this.initializeSocket();
  }

  private initializeSocket() {
    // 기존 소켓이 있으면 정리 후 새로 생성 (상태 동기화)
    if (this.socket) {
      this.debugLog('♻️ [AuctionSocketService] 기존 소켓 정리 후 재초기화', {
        socketId: this.socket.id,
        wasConnected: this.isConnected,
        actuallyConnected: this.socket.connected
      });
      try {
        this.socket.disconnect();
      } catch (e) {
        devWarn('♻️ [AuctionSocketService] 기존 소켓 정리 중 오류:', e);
      }
      this.socket = null;
      this.isConnected = false;
    }

    // 토큰이 없어도 게스트로 연결 허용 (서버에서 지원)
    this.debugLog('🔧 [AuctionSocketService] 소켓 초기화 시작:', {
      hasToken: !!this.accessToken,
      tokenType: this.tokenType
    });

    // 🔍 토큰 검증 및 디코딩 (토큰이 있을 때만)
    if (this.accessToken) {
      try {
        const tokenParts = this.accessToken.split('.');
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]));
          this.debugLog('🔍 [AuctionSocketService] JWT 토큰 정보:', {
            만료시간: new Date(payload.exp * 1000),
            현재시간: new Date(),
            유효여부: payload.exp * 1000 > Date.now(),
            토큰타입: this.tokenType,
            사용자정보: payload
          });
        }
      } catch (e) {
        devWarn('🔍 [AuctionSocketService] JWT 토큰 디코딩 실패:', e);
      }
    } else {
      this.debugLog('👤 [AuctionSocketService] 게스트 모드로 연결');
    }

    // 서버 URL 설정 (환경에 따라 변경) - /auction 네임스페이스 추가
    const baseUrl = process.env.REACT_APP_SOCKET_URL || 'http://localhost:3000';
    const serverUrl = `${baseUrl}/auction`;
    
    this.debugLog('🌐 [AuctionSocketService] 소켓 연결 엔드포인트:', serverUrl);
    this.debugLog('🔧 [AuctionSocketService] 환경변수 REACT_APP_SOCKET_URL:', process.env.REACT_APP_SOCKET_URL);
    this.debugLog('🔑 [AuctionSocketService] 인증 토큰 타입:', this.tokenType);
    this.debugLog('🔑 [AuctionSocketService] 인증 토큰:', this.accessToken ? `Bearer ${this.accessToken.substring(0, 20)}...` : '없음');
    this.debugLog('🔑 [AuctionSocketService] 토큰 전체 길이:', this.accessToken?.length);

    // 소켓 설정 - 토큰이 없어도 게스트로 연결
    const socketConfig: any = {
      transports: ['websocket', 'polling'],
      autoConnect: false,
      forceNew: true, // 새로운 연결 강제
      timeout: 20000, // 20초 타임아웃
      upgrade: true,
    };

    // 토큰이 있는 경우에만 인증 정보 추가
    if (this.accessToken) {
      socketConfig.auth = {
        token: this.accessToken,
        authorization: this.accessToken,
        Authorization: this.accessToken,
        tokenType: this.tokenType,
      };
      socketConfig.query = {
        token: this.accessToken,
        authorization: this.accessToken,
        tokenType: this.tokenType,
      };
    }

    this.socket = io(serverUrl, socketConfig);

    this.debugLog('⚙️ [AuctionSocketService] 소켓 설정 완료:', {
      url: serverUrl,
      transports: ['websocket', 'polling'],
      '토큰길이': this.accessToken?.length || 0,
      '토큰시작': this.accessToken ? this.accessToken.substring(0, 20) + '...' : '없음 (게스트)',
      '토큰타입': this.tokenType,
      autoConnect: false,
      '인증설정': {
        auth: !!this.accessToken,
        query: !!this.accessToken
      }
    });

    // 🔍 실제 연결 파라미터 로깅
    this.debugLog('🔗 [AuctionSocketService] 실제 연결 파라미터:');
    this.debugLog('🌐 서버 URL:', serverUrl);
    this.debugLog('🔑 토큰 (auth):', this.accessToken ? `${this.accessToken.substring(0, 30)}...` : '없음 (게스트 모드)');
    this.debugLog('🎭 토큰 타입:', this.tokenType);
    this.debugLog('📡 전송 방식:', ['websocket', 'polling']);
    this.debugLog('⚡ 자동연결:', false);

    this.setupEventHandlers();
  }

  private setupEventHandlers() {
    if (!this.socket) return;

    // � 모든 이벤트를 캐치하여 서버 응답 분석
    const socket = this.socket as any;
    const originalOnevent = socket.onevent;
    socket.onevent = (packet: any) => {
      devLog('🔴 [AuctionSocketService] ===== 서버 이벤트 수신 =====');
      devLog('📦 패킷 타입:', packet.type);
      devLog('🌐 네임스페이스:', packet.nsp);
      devLog('📊 데이터 전체:', packet.data);
      devLog('📋 패킷 전체:', JSON.stringify(packet, null, 2));
      
      if (packet.data && Array.isArray(packet.data)) {
        const [eventName, eventData] = packet.data;
        devLog('🎯 === 이벤트 상세 분석 ===');
        devLog('� 이벤트명:', eventName);
        devLog('📄 이벤트 데이터:', eventData);
        devLog('🔍 데이터 타입:', typeof eventData);
        devLog('🗂️ 데이터 구조:', JSON.stringify(eventData, null, 2));
        
        // 입찰 관련 이벤트인지 확인
        if (eventName && typeof eventName === 'string') {
          devLog('🔍 === 입찰 관련성 검사 ===');
          const bidKeywords = ['bid', 'place', 'auction', 'lot', 'history'];
          const isBidRelated = bidKeywords.some(keyword => {
            const matches = eventName.toLowerCase().includes(keyword.toLowerCase());
            devLog(`🔍 키워드 "${keyword}" 매칭:`, matches);
            return matches;
          });
          
          devLog('🎯 입찰 관련 이벤트 여부:', isBidRelated);
          
          // bid-history 이벤트 특별 처리
          if (eventName.toLowerCase().includes('history')) {
            devLog('📚 === bid-history 관련 이벤트 감지! ===');
            devLog('📛 감지된 이벤트명:', eventName);
            devLog('📄 감지된 데이터:', eventData);
            devLog('📊 데이터 타입:', typeof eventData);
            devLog('📋 데이터 구조:', eventData ? Object.keys(eventData) : 'null');
          }
          
          // 실제 입찰 데이터만 bid-placed로 처리 (경매 정보는 제외)
          if (isBidRelated && eventData && 
              !eventName.toLowerCase().includes('history') &&
              !eventName.toLowerCase().includes('auction-info') &&
              !eventName.toLowerCase().includes('get-auction') &&
              !eventName.toLowerCase().includes('info') &&
              typeof eventData === 'object' &&
              'lotIndex' in eventData && 'price' in eventData && 'code' in eventData &&
              'userIndex' in eventData) {
            devLog('💰 === 실제 입찰 이벤트 감지! ===');
            devLog('📛 감지된 이벤트명:', eventName);
            devLog('📄 감지된 데이터:', eventData);
            this.emit('bid-placed', eventData);
          } else if (isBidRelated && eventData) {
            devLog('🔍 === 입찰 관련이지만 필터링됨 ===');
            devLog('📛 이벤트명:', eventName);
            devLog('📄 이유:', {
              hasHistory: eventName.toLowerCase().includes('history'),
              hasAuctionInfo: eventName.toLowerCase().includes('auction-info'),
              hasGetAuction: eventName.toLowerCase().includes('get-auction'),
              hasInfo: eventName.toLowerCase().includes('info'),
              hasLotIndex: 'lotIndex' in eventData,
              hasPrice: 'price' in eventData,
              hasCode: 'code' in eventData,
              hasUserIndex: 'userIndex' in eventData
            });
          }
        }
      }
      
      return originalOnevent.call(socket, packet);
    };

    // 연결 성공
    this.socket.on('connect', () => {
      const baseUrl = process.env.REACT_APP_SOCKET_URL || 'http://localhost:3000';
      const serverUrl = `${baseUrl}/auction`;
      devLog('✅ [AuctionSocketService] 소켓 연결 성공!');
      devLog('🌐 연결된 엔드포인트:', serverUrl);
      devLog('🆔 소켓 ID:', this.socket?.id);
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this.emit('connected', true);
      
      // 🔍 연결 후 즉시 bid-placed 이벤트 리스너 등록 확인
      devLog('🎯 [AuctionSocketService] bid-placed 이벤트 리스너 등록 확인');
      this.socket?.on('bid-placed', (data: any) => {
        devLog('💰 [DIRECT] bid-placed 이벤트 수신!', data);
        this.emit('bid-placed', data);
      });
    });

    // 연결 해제
    this.socket.on('disconnect', (reason: string) => {
      devLog('❌ 소켓 연결 해제:', reason);
      this.isConnected = false;
      this.emit('connected', false);
      
      // 의도적인 연결 해제가 아닌 경우 재연결 시도
      if (reason === 'io server disconnect') {
        this.attemptReconnect();
      }
    });

    // 연결 에러
    this.socket.on('connect_error', (error: Error) => {
      const baseUrl = process.env.REACT_APP_SOCKET_URL || 'http://localhost:3000';
      const serverUrl = `${baseUrl}/auction`;
      devError('🔴 [AuctionSocketService] 소켓 연결 에러!');
      devError('🌐 시도한 엔드포인트:', serverUrl);
      devError('❌ 에러 내용:', error.message);
      devError('🔍 전체 에러:', error);
      
      // 연결 에러 메시지에서 인증 관련 에러인지 확인
      const isAuthError = error.message?.includes('인증') || 
                         error.message?.includes('토큰') || 
                         error.message?.includes('로그인') ||
                         error.message?.includes('Invalid') ||
                         error.message?.includes('유효하지') ||
                         error.message?.includes('필요합니다');
      
      if (isAuthError) {
        devError('🚫 [AuctionSocketService] 인증 관련 연결 에러 감지');
        // 인증 에러로 처리
        this.emit('auth-error', {
          message: error.message,
          code: 'AUTH_REQUIRED',
          error: error,
          timestamp: new Date().toISOString()
        });
      }
      
      this.isConnected = false;
      this.emit('connected', false);
      this.attemptReconnect();
    });

    // 서버에서 오는 이벤트들 처리 - 올바른 응답 이벤트명들
    this.socket.on('get-auction-info-response', (response: ResponseModel<AuctionDetailView>) => {
      devLog('📥 [AuctionSocketService] 경매 정보 응답 수신 (get-auction-info-response):', response);
      this.emit('auction-info-received', response);
    });

    // 다른 가능한 응답 이벤트명들도 추가로 리스닝
    this.socket.on('auction-info', (response: ResponseModel<AuctionDetailView>) => {
      devLog('📥 [AuctionSocketService] 경매 정보 수신 (auction-info):', response);
      this.emit('auction-info-received', response);
    });

    this.socket.on('get-auction-info', (response: ResponseModel<AuctionDetailView>) => {
      devLog('📥 [AuctionSocketService] 경매 정보 수신 (get-auction-info):', response);
      this.emit('auction-info-received', response);
    });

    // 모든 서버 이벤트를 캐치하여 로깅
    const allServerEvents = [
      'get-auction-info', 'auction-info', 'get-auction-info-response',
      'not-found', 'error', 'forbidden', 'bid-history', 'bid-placed',
      'bid-started', 'next-lot', 'auction-ended', 'ban-user'
    ];

    allServerEvents.forEach(eventName => {
      this.socket?.on(eventName, (data: any) => {
        devLog(`📡 [AuctionSocketService] 서버 이벤트 '${eventName}' 수신:`, data);
      });
    });

    this.socket.on('not-found', (errorResponse: any) => {
      devLog('❌ [AuctionSocketService] 경매 없음 (not-found):', errorResponse);
      devLog('❌ [AuctionSocketService] not-found 상세:', {
        전체응답: errorResponse,
        에러메시지: errorResponse?.message || errorResponse?.error?.message,
        에러코드: errorResponse?.error?.code,
        데이터: errorResponse?.data,
        성공여부: errorResponse?.success,
        타입: typeof errorResponse
      });
      
      // bid-history 관련 not-found는 빈 배열로 처리 (입찰 내역이 없는 경우)
      if (errorResponse?.error?.customMessage?.includes('입찰 내역')) {
        devLog('📚 [AuctionSocketService] 입찰 내역이 없음 - 빈 배열로 처리');
        this.emit('bid-history', []);
      } else {
        this.emit('auction-not-found', errorResponse);
      }
    });

    this.socket.on('error', (errorResponse: any) => {
      devError('🔴 [AuctionSocketService] 소켓 에러 (error):', errorResponse);
      devError('🔍 [AuctionSocketService] 에러 상세 정보:', {
        전체응답: errorResponse,
        에러메시지: errorResponse?.message || errorResponse?.error?.message,
        에러코드: errorResponse?.error?.code,
        데이터: errorResponse?.data,
        성공여부: errorResponse?.success,
        타입: typeof errorResponse,
        이벤트명: 'error'
      });
      this.emit('auction-error', errorResponse);
    });

    this.socket.on('forbidden', (errorResponse: any) => {
      devError('🔒 [AuctionSocketService] 권한 없음 (forbidden):', errorResponse);
      devError('🔍 [AuctionSocketService] forbidden 상세:', {
        전체응답: errorResponse,
        에러메시지: errorResponse?.message || errorResponse?.error?.message,
        관리자인증필요: '관리자 권한이 필요할 수 있습니다',
        타입: typeof errorResponse,
        이벤트명: 'forbidden'
      });
      this.emit('auction-error', errorResponse);
    });

    // 입찰 내역 이벤트 (중요!)
    this.socket.on('bid-history', (response: any) => {
      devLog('📚 [AuctionSocketService] bid-history 이벤트 수신!', response);
      devLog('📚 [AuctionSocketService] bid-history 타입:', typeof response);
      devLog('📚 [AuctionSocketService] bid-history 키목록:', response ? Object.keys(response) : 'null');
      
      // 명세에 따른 ResponseModel 구조 확인
      if (response && response.statusCode === 200 && response.data && Array.isArray(response.data)) {
        devLog('📚 [AuctionSocketService] ✅ 정상적인 BidHistoryResponse 구조');
        devLog('📚 [AuctionSocketService] 입찰 내역 데이터:', response.data.length, '개');
        if (response.data.length > 0) {
          devLog('📚 [AuctionSocketService] 첫 번째 입찰:', response.data[0]);
        }
        this.emit('bid-history', response.data);
      } else if (response && Array.isArray(response)) {
        // 배열 형태로 직접 수신하는 경우 (백업 처리)
        devLog('📚 [AuctionSocketService] ✅ 배열 형태로 직접 수신');
        devLog('📚 [AuctionSocketService] 입찰 내역 데이터:', response.length, '개');
        if (response.length > 0) {
          devLog('📚 [AuctionSocketService] 첫 번째 입찰:', response[0]);
        }
        this.emit('bid-history', response);
      } else {
        devError('❌ [AuctionSocketService] bid-history 예상하지 못한 구조:', response);
        // 에러인 경우에도 빈 배열로 처리
        this.emit('bid-history', []);
      }
    });

    this.socket.on('bid-placed', (bidData: any) => {
      devLog('💰 [AuctionSocketService] bid-placed 이벤트 수신!');
      devLog('🔍 [AuctionSocketService] bid-placed 원본 데이터:', JSON.stringify(bidData, null, 2));
      
      // 명세에 따른 BidPlacedData 형태 검증
      const isValidBidData = bidData && 
                            typeof bidData === 'object' && 
                            'lotIndex' in bidData && 
                            'price' in bidData && 
                            'code' in bidData &&
                            'userIndex' in bidData &&
                            'userName' in bidData &&
                            'userEmail' in bidData &&
                            'userCellphone' in bidData &&
                            'usdPrice' in bidData;
      
      devLog('🔍 [AuctionSocketService] bid-placed 데이터 유효성:', {
        유효함: isValidBidData,
        타입: typeof bidData,
        키목록: bidData ? Object.keys(bidData) : '없음',
        lotIndex: bidData?.lotIndex,
        price: bidData?.price,
        code: bidData?.code,
        userIndex: bidData?.userIndex,
        usdPrice: bidData?.usdPrice
      });
      
      if (isValidBidData) {
        devLog('✅ [AuctionSocketService] 유효한 bid-placed 데이터 전달');
        this.emit('bid-placed', bidData);
      } else {
        devWarn('❌ [AuctionSocketService] bid-placed 데이터가 명세와 맞지 않음, 무시');
        devWarn('📋 [AuctionSocketService] 예상 형태: { lotIndex, price, usdPrice, userIndex, userName, userEmail, userCellphone, code }');
      }
    });

    // 입찰 관련 추가 이벤트들 - 명세 외 이벤트들 모니터링
    const bidEvents = [
      'bid-accepted', 'bid-rejected', 'place-bid-response', 
      'bid-error', 'bid-success', 'bid-fail', 'bidding-result'
    ];
    
    bidEvents.forEach(eventName => {
      this.socket?.on(eventName, (data: any) => {
        devLog(`📡 [AuctionSocketService] ${eventName} 이벤트 수신:`, JSON.stringify(data, null, 2));
        
        // 에러 이벤트는 auction-error로 전달
        if (eventName.includes('error') || eventName.includes('fail') || eventName.includes('rejected')) {
          devError(`🚨 [AuctionSocketService] ${eventName} 에러 이벤트 -> auction-error로 전달:`, data);
          this.emit('auction-error', data);
        } else {
          // 성공 이벤트는 추가적인 정보로 로깅만
          devLog(`✅ [AuctionSocketService] ${eventName} - 추가 정보`);
        }
      });
    });

    // 관리자 전용 이벤트들 처리
    this.socket.on('bid-started', (data: any) => {
      devLog('🏁 [AuctionSocketService] 작품 경매 시작:', data);
      this.emit('bid-started', data);
    });

    this.socket.on('next-lot', (data: any) => {
      devLog('⏭️ [AuctionSocketService] 다음 작품 정보:', data);
      this.emit('next-lot', data);
    });

    this.socket.on('auction-ended', (data: any) => {
      devLog('🏁 [AuctionSocketService] 경매 종료:', data);
      this.emit('auction-ended', data);
    });

    this.socket.on('ban-user', (data: any) => {
      devLog('🚫 [AuctionSocketService] 사용자 차단됨:', data);
      this.emit('ban-user', data);
    });

    // ban-user-list 이벤트 핸들러 추가
    this.socket.on('ban-user-list', (data: any) => {
      devLog('📋 [AuctionSocketService] 차단된 사용자 목록 수신:', data);
      this.emit('ban-user-list', data);
    });

    // unban-user 이벤트 핸들러 추가 (없는 경우를 대비)
    this.socket.on('unban-user', (data: any) => {
      devLog('✅ [AuctionSocketService] 사용자 차단 해제됨:', data);
      this.emit('unban-user', data);
    });

    // 10초 카운트다운 관련 이벤트들
    this.socket.on('start-countdown', (data: any) => {
      devLog('⏰ [AuctionSocketService] 카운트다운 시작:', data);
      this.emit('start-countdown', data);
    });

    this.socket.on('countdown-tick', (data: any) => {
      devLog('⏱️ [AuctionSocketService] 카운트다운 틱:', data);
      this.emit('countdown-tick', data);
    });

    this.socket.on('countdown-ended', (data: any) => {
      devLog('⏰ [AuctionSocketService] 카운트다운 종료:', data);
      this.emit('countdown-ended', data);
    });

    this.socket.on('pause-countdown', (data: any) => {
      devLog('⏸️ [AuctionSocketService] 카운트다운 일시정지:', data);
      this.emit('pause-countdown', data);
    });

    this.socket.on('resume-countdown', (data: any) => {
      devLog('▶️ [AuctionSocketService] 카운트다운 재개:', data);
      this.emit('resume-countdown', data);
    });

    this.socket.on('stop-countdown', (data: any) => {
      devLog('⏹️ [AuctionSocketService] 카운트다운 중지:', data);
      this.emit('stop-countdown', data);
    });

    // 타이머 제어 응답 이벤트들
    this.socket.on('timer-control-response', (response: any) => {
      devLog('🎮 [AuctionSocketService] 타이머 제어 응답:', response);
      this.emit('timer-control-response', response);
    });

    this.socket.on('timer-status-response', (response: any) => {
      devLog('📊 [AuctionSocketService] 타이머 상태 응답:', response);
      this.emit('timer-status-response', response);
    });

    this.socket.on('forbidden', (errorResponse: any) => {
      devLog('🔒 [AuctionSocketService] 권한 없음:', errorResponse);
      this.emit('auction-error', errorResponse);
    });

    // 인증 에러 처리 (토큰 만료/무효 시)
    this.socket.on('auth-error', (errorResponse: any) => {
      devLog('🚫 [AuctionSocketService] 인증 에러 (auth-error):', errorResponse);
      devLog('🔍 [AuctionSocketService] 인증 에러 상세:', {
        전체응답: errorResponse,
        에러메시지: errorResponse?.message || errorResponse?.error?.message,
        에러코드: errorResponse?.code || errorResponse?.error?.code,
        reconnectRequired: errorResponse?.reconnectRequired,
        타입: typeof errorResponse
      });
      
      // 인증 에러는 특별히 처리
      this.emit('auth-error', errorResponse);
    });

    // � 명세 기반 핵심 이벤트들만 처리하고 나머지는 정리
  }

  private attemptReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      devError('🔴 최대 재연결 시도 횟수 초과');
      this.emit('max-reconnect-attempts-reached', null);
      return;
    }

    this.reconnectAttempts++;
    devLog(`🔄 재연결 시도 ${this.reconnectAttempts}/${this.maxReconnectAttempts}...`);
    
    setTimeout(() => {
      this.connect();
    }, this.reconnectDelay * this.reconnectAttempts);
  }

  // 공용 메서드들
  connect() {
    devLog('🚀 [AuctionSocketService] 소켓 연결 시도 시작');
    
    if (!this.socket) {
      devLog('🔧 소켓 인스턴스가 없어서 새로 초기화합니다.');
      this.initializeSocket();
    }

    if (this.socket) {
      const actuallyConnected = this.socket.connected === true;

      if (!actuallyConnected) {
        devLog('📡 소켓 연결 실행 중...', {
          isConnectedFlag: this.isConnected,
          socketConnected: this.socket.connected
        });
        this.isConnected = false; // 실제 상태에 맞게 플래그 동기화
        this.socket.connect();
      } else {
        devLog('✅ 이미 소켓이 연결되어 있습니다.', {
          isConnectedFlag: this.isConnected,
          socketConnected: this.socket.connected
        });
        this.isConnected = true; // 실제 상태에 맞게 플래그 동기화
      }
    } else {
      devWarn('⚠️ 소켓 인스턴스를 생성할 수 없습니다.');
    }
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.isConnected = false;
    }
  }

    // 경매 정보 요청
  requestAuctionInfo(auctionIndex: number): void {
    devLog('📤 [AuctionSocketService] 경매 정보 요청 시작:', {
      auctionIndex,
      auctionIndexType: typeof auctionIndex,
      auctionIndexValid: auctionIndex !== undefined && auctionIndex !== null && !isNaN(auctionIndex)
    });
    this.debugLog('🔌 [AuctionSocketService] 현재 소켓 상태:', {
      isConnected: this.isConnected,
      socketId: this.socket?.id || 'none',
      connected: this.socket?.connected || false,
      네임스페이스: this.socket?.nsp || 'unknown'
    });
    
    // auctionIndex 유효성 검사 추가
    if (auctionIndex === undefined || auctionIndex === null || isNaN(auctionIndex)) {
      devError('❌ [AuctionSocketService] 유효하지 않은 auctionIndex:', {
        auctionIndex,
        type: typeof auctionIndex,
        isNaN: isNaN(auctionIndex)
      });
      return;
    }
    
    // isConnected 플래그로만 확인 (socket.connected는 때때로 늦게 업데이트됨)
    if (!this.socket || !this.isConnected) {
      devError('❌ [AuctionSocketService] 소켓이 연결되지 않음');
      return;
    }

    // 소켓 객체가 존재하면 요청 진행 (connected 상태와 무관하게)
    const payload = { auctionIndex };
    devLog('📡 [AuctionSocketService] get-auction-info 전송 데이터:', {
      payload,
      payloadString: JSON.stringify(payload),
      auctionIndexInPayload: payload.auctionIndex
    });
    
    try {
      this.socket.emit('get-auction-info', payload);
      devLog('✅ [AuctionSocketService] get-auction-info 이벤트 전송 완료');
    } catch (error) {
      devError('❌ [AuctionSocketService] get-auction-info 전송 실패:', error);
    }
    
    // 콜백 형식으로도 시도
    try {
      this.socket.emit('get-auction-info', payload, (response: any) => {
        this.debugLog('📞 [AuctionSocketService] get-auction-info 콜백 응답:', response);
        if (response) {
          this.emit('auction-info-received', response);
        }
      });
      this.debugLog('✅ [AuctionSocketService] get-auction-info 콜백 전송 완료');
    } catch (error) {
      devError('❌ [AuctionSocketService] get-auction-info 콜백 전송 실패:', error);
    }
  }

  // 경매방 참여
  joinAuction(auctionIndex: number) {
    devLog('📤 [AuctionSocketService] 경매방 참여 요청 시작:', {
      auctionIndex,
      auctionIndexType: typeof auctionIndex,
      auctionIndexValid: auctionIndex !== undefined && auctionIndex !== null && !isNaN(auctionIndex)
    });
    
    // auctionIndex 유효성 검사 추가
    if (auctionIndex === undefined || auctionIndex === null || isNaN(auctionIndex)) {
      devError('❌ [AuctionSocketService] joinAuction - 유효하지 않은 auctionIndex:', {
        auctionIndex,
        type: typeof auctionIndex,
        isNaN: isNaN(auctionIndex)
      });
      return;
    }
    
    if (!this.isConnected || !this.socket) {
      devWarn('❌ [AuctionSocketService] 소켓이 연결되지 않았습니다. 경매방에 참여할 수 없습니다.');
      return;
    }

    const requestData = { auctionIndex };
    devLog('� [AuctionSocketService] join-auction 전송 데이터:', {
      requestData,
      requestDataString: JSON.stringify(requestData),
      auctionIndexInRequest: requestData.auctionIndex
    });
    devLog('🔌 [AuctionSocketService] 현재 소켓 상태:', { 
      isConnected: this.isConnected, 
      socketId: this.socket.id,
      connected: this.socket.connected,
      네임스페이스: this.socket.nsp
    });
    
    try {
      this.socket.emit('join-auction', requestData);
      devLog('✅ [AuctionSocketService] join-auction 이벤트 전송 완료');
    } catch (error) {
      devError('❌ [AuctionSocketService] join-auction 전송 실패:', error);
    }
  }

  // 입찰 내역 조회
  getBidHistory(auctionIndex: number, lotIndex: number) {
    if (!this.isConnected || !this.socket) {
      devWarn('❌ [AuctionSocketService] 소켓이 연결되지 않았습니다. 입찰 내역을 조회할 수 없습니다.');
      return;
    }

    const requestData = { auctionIndex, lotIndex };
    devLog('📚 [AuctionSocketService] 입찰 내역 조회 시작:', requestData);
    devLog('🔌 [AuctionSocketService] 현재 소켓 상태:', { 
      isConnected: this.isConnected, 
      socketId: this.socket.id,
      connected: this.socket.connected 
    });
    
    // 명세에 따라 bid-history 이벤트로 요청
    devLog('📡 [AuctionSocketService] bid-history 이벤트 전송:', requestData);
    this.socket.emit('bid-history', requestData);
  }

  // 입찰하기
  placeBid(auctionIndex: number, lotIndex: number, price: number) {
    if (!this.isConnected || !this.socket) {
      devWarn('❌ [AuctionSocketService] 소켓이 연결되지 않았습니다. 입찰할 수 없습니다.');
      return;
    }

    const bidData = { auctionIndex, lotIndex, price };
    devLog('📤 [AuctionSocketService] 입찰 요청:', bidData);
    devLog('🔌 [AuctionSocketService] 현재 소켓 상태:', { 
      isConnected: this.isConnected, 
      socketId: this.socket.id,
      connected: this.socket.connected,
      네임스페이스: this.socket.nsp
    });

    // 콜백을 사용한 입찰 요청 (중복 방지를 위해 하나만 사용)
    devLog('📡 [AuctionSocketService] place-bid 이벤트 전송 (콜백 포함):', bidData);
    this.socket.emit('place-bid', bidData, (response: any) => {
      devLog('📞 [AuctionSocketService] place-bid 콜백 응답:', response);
      if (response) {
        if (response.success !== false) {
          devLog('✅ 입찰 성공 응답 수신');
        } else {
          devError('❌ 입찰 실패 콜백 응답 -> auction-error로 전달:', response);
          this.emit('auction-error', response);
        }
      }
    });
  }

  // ============= 관리자 전용 기능들 =============

  // 작품 경매 시작 (관리자 전용)
  startBid(auctionIndex: number, lotNo: number) {
    if (!this.isConnected || !this.socket) {
      devWarn('❌ [AuctionSocketService] 소켓이 연결되지 않았습니다.');
      return;
    }

    const requestData = { auctionIndex, lotNo };
    devLog('🏁 [AuctionSocketService] 작품 경매 시작:', requestData);
    this.socket.emit('start-bid', requestData);
  }

  // CMS에서 현장입찰 추가 (관리자 전용)
  cmsPlaceBid(auctionIndex: number, lotIndex: number, price: number, code: string) {
    if (!this.isConnected || !this.socket) {
      devWarn('❌ [AuctionSocketService] 소켓이 연결되지 않았습니다.');
      return;
    }

    const requestData = { auctionIndex, lotIndex, price, code };
    devLog('🏢 [AuctionSocketService] CMS 현장입찰 추가:', requestData);
    this.socket.emit('cms-place-bid', requestData);
  }

  // 타이머 제어 (관리자 전용)
  timerControl(auctionIndex: number, lotIndex: number, action: 'start' | 'pause' | 'resume' | 'reset' | 'stop', duration?: number) {
    if (!this.isConnected || !this.socket) {
      devWarn('❌ [AuctionSocketService] 소켓이 연결되지 않았습니다.');
      return;
    }

    const requestData: any = { auctionIndex, lotIndex, action };
    if (action === 'start' || action === 'reset') {
      requestData.duration = duration || 10; // 기본 10초
    }

    devLog('🎮 [AuctionSocketService] 타이머 제어:', requestData);
    this.socket.emit('timer-control', requestData);
  }

  // 타이머 상태 조회 (관리자 전용)
  getTimerStatus(auctionIndex: number, lotIndex?: number) {
    if (!this.isConnected || !this.socket) {
      devWarn('❌ [AuctionSocketService] 소켓이 연결되지 않았습니다.');
      return;
    }

    const requestData: any = { auctionIndex };
    if (lotIndex !== undefined) {
      requestData.lotIndex = lotIndex;
    }

    devLog('📊 [AuctionSocketService] 타이머 상태 조회:', requestData);
    this.socket.emit('get-timer-status', requestData);
  }

  // 사용자 차단 (관리자 전용)
  banUser(auctionIndex: number, userIndex: number) {
    if (!this.isConnected || !this.socket) {
      devWarn('❌ [AuctionSocketService] 소켓이 연결되지 않았습니다.');
      return;
    }

    const requestData = { auctionIndex, userIndex };
    devLog('🚫 [AuctionSocketService] 사용자 차단:', requestData);
    this.socket.emit('ban-user', requestData);
  }

  // 사용자 차단 해제 (관리자 전용)
  unbanUser(auctionIndex: number, userIndex: number) {
    if (!this.isConnected || !this.socket) {
      devWarn('❌ [AuctionSocketService] 소켓이 연결되지 않았습니다.');
      return;
    }

    const requestData = { auctionIndex, userIndex };
    devLog('✅ [AuctionSocketService] 사용자 차단 해제:', requestData);
    this.socket.emit('unban-user', requestData);
  }

  // 차단된 사용자 목록 조회 (관리자 전용)
  getBanUserList(auctionIndex: number) {
    if (!this.isConnected || !this.socket) {
      devWarn('❌ [AuctionSocketService] 소켓이 연결되지 않았습니다.');
      return;
    }

    const requestData = { auctionIndex };
    devLog('📋 [AuctionSocketService] 차단된 사용자 목록 조회:', requestData);
    this.socket.emit('ban-user-list', requestData);
  }

  // 다음 작품으로 이동 (관리자 전용)
  nextLot(auctionIndex: number) {
    if (!this.isConnected || !this.socket) {
      devWarn('❌ [AuctionSocketService] 소켓이 연결되지 않았습니다.');
      return;
    }

    const requestData = { auctionIndex };
    devLog('⏭️ [AuctionSocketService] 다음 작품으로 이동:', requestData);
    this.socket.emit('next-lot', requestData);
  }

  // 10초 카운트다운 시작 (간단한 버전 - 호환성 유지)
  auctionCountdown() {
    if (!this.isConnected || !this.socket) {
      devWarn('❌ [AuctionSocketService] 소켓이 연결되지 않았습니다.');
      return;
    }

    devLog('⏰ [AuctionSocketService] 경매 카운트다운 시작 (레거시)');
    this.socket.emit('auction-countdown');
  }

  // 이벤트 리스너 등록
  on(event: string, callback: Function) {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
    this.eventListeners.get(event)?.push(callback);
  }

  // 이벤트 리스너 제거
  off(event: string, callback?: Function) {
    if (!callback) {
      this.eventListeners.delete(event);
      return;
    }

    const listeners = this.eventListeners.get(event);
    if (listeners) {
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
  }

  // 이벤트 발생
  private emit(event: string, data: any) {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.forEach(callback => callback(data));
    }
  }

  // 연결 상태 확인
  isSocketConnected() {
    return this.isConnected;
  }

  // 소켓 ID 가져오기
  getSocketId() {
    return this.socket?.id;
  }

  // 리소스 정리
  destroy() {
    this.disconnect();
    this.eventListeners.clear();
    this.socket = null;
  }
}

// 싱글톤 인스턴스
export const auctionSocketService = new AuctionSocketService();
export default auctionSocketService;
